import { useEffect, useState } from 'react';
import { Shield, Mail, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { fetchPublicStats } from '../services/api';
import { buildLoginRedirectUrl } from '../services/appUrls';
import { getExperimentVariant, trackLoginStart } from '../services/analytics';

const Hero = () => {
  const { i18n, t } = useTranslation();
  const [variant] = useState(() => getExperimentVariant());
  const [publicAliasCount, setPublicAliasCount] = useState<number | null>(null);
  const copyKey = variant === 'privacy_first' ? 'landing.heroPrivacy' : 'landing.hero';

  useEffect(() => {
    let mounted = true;

    fetchPublicStats()
      .then((data) => {
        const count = Number(data?.result?.aliasCount);
        if (mounted && Number.isFinite(count) && count > 0) {
          setPublicAliasCount(count);
        }
      })
      .catch(() => {
        if (mounted) setPublicAliasCount(null);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const formattedAliasCount =
    publicAliasCount === null
      ? null
      : new Intl.NumberFormat(i18n.language || undefined).format(publicAliasCount);

  return (
    <section className="pt-32 pb-16 md:pt-40 md:pb-24 bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex flex-col md:flex-row items-center">
          <div className="md:w-1/2 mb-10 md:mb-0">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-gray-900 leading-tight">
              {t(`${copyKey}.title`)}
            </h1>
            <p className="text-xl md:text-2xl text-gray-700 mb-8 leading-relaxed">
              {t(`${copyKey}.subtitle`)}
            </p>
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
              <a
                href={buildLoginRedirectUrl()}
                onClick={trackLoginStart}
                className="bg-blue-600 text-white px-6 py-3 rounded-md font-medium text-center hover:bg-blue-700 transition-colors"
              >
                {t(`${copyKey}.getStarted`)}
              </a>
              <a
                href="#how-it-works"
                className="border border-gray-300 text-gray-700 px-6 py-3 rounded-md font-medium text-center hover:bg-gray-100 transition-colors flex items-center justify-center"
              >
                {t(`${copyKey}.learnMore`)} <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </div>
            {formattedAliasCount && (
              <p className="mt-5 text-sm font-medium text-gray-600">
                {t('landing.hero.stats.aliasesServed', { aliasCount: formattedAliasCount })}
              </p>
            )}
          </div>
          <div className="md:w-1/2 md:pl-10 flex justify-center">
            <div className="relative">
              <div className="absolute -inset-0.5 bg-blue-600/20 rounded-xl blur-xl opacity-70"></div>
              <div className="relative bg-white p-6 rounded-xl shadow-xl">
                <div className="flex items-center justify-center mb-4">
                  <Shield className="h-16 w-16 text-blue-600" />
                  <Mail className="h-12 w-12 text-blue-600 -ml-6 mt-6" />
                </div>
                <div className="space-y-4">
                  <div className="flex items-center p-3 bg-gray-100 rounded-lg">
                    <span className="text-sm font-medium text-gray-800">{t(`${copyKey}.demo.original`)}:</span>
                    <span className="ml-2 text-sm text-gray-600">you@gmail.com</span>
                  </div>
                  <div className="flex items-center justify-center">
                    <ArrowDown />
                  </div>
                  <div className="flex items-center p-3 bg-blue-100 rounded-lg">
                    <span className="text-sm font-medium text-gray-800">{t(`${copyKey}.demo.alias`)}:</span>
                    <span className="ml-2 text-sm text-blue-600">amazon@you.uu.me</span>
                  </div>
                  <div className="flex items-center justify-center">
                    <ArrowDown />
                  </div>
                  <div className="flex items-center p-3 bg-green-100 rounded-lg">
                    <span className="text-sm font-medium text-gray-800">{t(`${copyKey}.demo.protection`)}:</span>
                    <span className="ml-2 text-sm text-green-600">{t(`${copyKey}.demo.protectionDetails`)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const ArrowDown = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="text-gray-400"
  >
    <line x1="12" y1="5" x2="12" y2="19"></line>
    <polyline points="19 12 12 19 5 12"></polyline>
  </svg>
);

export default Hero;
